import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { getAuditLog } from '../lib/api';
import { toast } from 'sonner';
import {
  History, Filter, ChevronLeft, ChevronRight, User, Clock,
  Plus, Pencil, Trash2, ArrowRightLeft, Sliders
} from 'lucide-react';

const TUTTI = 'all';

const ENTITY_LABELS = {
  spesa: 'Spesa',
  chiusura: 'Chiusura',
  ordine: 'Ordine',
  articolo: 'Articolo',
  soglia: 'Soglia inventario',
  categoria_articolo: 'Categoria',
  fornitore: 'Fornitore',
  personale: 'Personale',
  lista_spesa: 'Lista spesa',
  checklist: 'Checklist',
  inventario: 'Inventario',
  user: 'Utente',
};

const ACTION_META = {
  create: { label: 'Creato', icon: Plus, color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/40' },
  update: { label: 'Modificato', icon: Pencil, color: 'bg-blue-500/15 text-blue-600 border-blue-500/40' },
  delete: { label: 'Eliminato', icon: Trash2, color: 'bg-red-500/15 text-red-600 border-red-500/40' },
  state_change: { label: 'Cambio stato', icon: ArrowRightLeft, color: 'bg-amber-500/15 text-amber-600 border-amber-500/40' },
  soglia_update: { label: 'Soglia', icon: Sliders, color: 'bg-purple-500/15 text-purple-600 border-purple-500/40' },
  upload: { label: 'Upload', icon: Plus, color: 'bg-slate-500/15 text-slate-600 border-slate-500/40' },
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const DetailsCell = ({ details, action }) => {
  if (!details || Object.keys(details).length === 0) return <span className="text-xs text-muted-foreground italic">—</span>;

  // Changes diff per update
  if (details.changes && Object.keys(details.changes).length > 0) {
    return (
      <div className="space-y-1 text-xs">
        {Object.entries(details.changes).slice(0, 4).map(([field, vals]) => (
          <div key={field} className="flex flex-wrap items-baseline gap-1">
            <span className="font-medium">{field}:</span>
            <span className="text-red-600 line-through">{String(vals.before ?? '—').slice(0, 30)}</span>
            <span className="text-muted-foreground">→</span>
            <span className="text-emerald-600">{String(vals.after ?? '—').slice(0, 30)}</span>
          </div>
        ))}
        {Object.keys(details.changes).length > 4 && (
          <p className="text-xs text-muted-foreground italic">+{Object.keys(details.changes).length - 4} altri campi</p>
        )}
      </div>
    );
  }

  // State change
  if (action === 'state_change' && details.stato_before) {
    return (
      <div className="text-xs">
        <Badge variant="outline" className="mr-1">{details.stato_before}</Badge>
        →
        <Badge variant="default" className="ml-1">{details.stato_after}</Badge>
      </div>
    );
  }

  // Soglia change
  if (action === 'soglia_update') {
    return (
      <div className="text-xs space-y-0.5">
        <div>Soglia: <span className="font-mono">{details.soglia_minima_before ?? 0} → {details.soglia_minima_after ?? 0}</span></div>
        {details.quantita_riordino_after !== undefined && (
          <div>Riordino: <span className="font-mono">{details.quantita_riordino_before ?? 0} → {details.quantita_riordino_after ?? 0}</span></div>
        )}
      </div>
    );
  }

  // Create/delete: mostra i campi chiave
  const keys = Object.keys(details).filter(k => details[k] !== null && details[k] !== undefined).slice(0, 3);
  return (
    <div className="text-xs space-y-0.5 text-muted-foreground">
      {keys.map((k) => (
        <div key={k}><span className="font-medium">{k}:</span> {String(details[k]).slice(0, 40)}</div>
      ))}
    </div>
  );
};

const StoricoAttivita = () => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState(TUTTI);
  const [action, setAction] = useState(TUTTI);
  const [page, setPage] = useState(0);
  const limit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAuditLog({
        entity_type: entityType !== TUTTI ? entityType : undefined,
        action: action !== TUTTI ? action : undefined,
        limit,
        skip: page * limit,
      });
      setData(res);
    } catch (error) {
      toast.error('Errore caricamento storico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, action, page]);

  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, action]);

  const totalPages = Math.ceil(data.total / limit);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="storico-attivita-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <History className="h-7 w-7" />
          Storico Attività
        </h1>
        <p className="text-muted-foreground mt-1">
          Tracciamento completo di tutte le azioni (chi-cosa-quando) su spese, chiusure, ordini, articoli e inventario.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtri:</span>
          </div>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="w-[180px]" data-testid="filter-entity">
              <SelectValue placeholder="Tipo entità" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TUTTI}>Tutte le entità</SelectItem>
              {Object.entries(ENTITY_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[180px]" data-testid="filter-action">
              <SelectValue placeholder="Azione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TUTTI}>Tutte le azioni</SelectItem>
              {Object.entries(ACTION_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="ml-auto text-sm text-muted-foreground">
            Totale: <span className="font-medium text-foreground">{data.total}</span>
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      ) : data.items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nessuna attività registrata con i filtri attuali.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Data/Ora</TableHead>
                    <TableHead>Utente</TableHead>
                    <TableHead>Azione</TableHead>
                    <TableHead>Entità</TableHead>
                    <TableHead className="hidden md:table-cell">Dettagli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((item) => {
                    const meta = ACTION_META[item.action] || ACTION_META.update;
                    const Icon = meta.icon;
                    return (
                      <TableRow key={item.id} data-testid={`audit-row-${item.id}`}>
                        <TableCell className="text-xs whitespace-nowrap font-mono">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            {formatDateTime(item.at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium text-sm">{item.user_nome || '—'}</span>
                            {item.user_role === 'admin' && (
                              <Badge variant="outline" className="text-xs h-5">admin</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`gap-1 ${meta.color}`}>
                            <Icon className="h-3 w-3" />
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{item.entity_label || '—'}</p>
                            <p className="text-xs text-muted-foreground">{ENTITY_LABELS[item.entity_type] || item.entity_type}</p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell max-w-[400px]">
                          <DetailsCell details={item.details} action={item.action} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Pagina {page + 1} di {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="audit-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Precedente
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              data-testid="audit-next"
            >
              Successiva
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoricoAttivita;
